// eslint-disable-next-line boundaries/external
import type { MetaDescriptor } from "react-router";

import type { GetSettingsQuery, Seo_Translations } from "~/core";

import type { Pages } from "../types";

export function prepareSeoData(
  params: {
    pageSeo: Pages["seo"];
    fallbackSeo?: Pages["seo"];
    originalUrl: string;
    currentLang: string;
    languages: GetSettingsQuery["languages"];
  },
  override?: Partial<Seo_Translations>,
): Array<MetaDescriptor> {
  const { pageSeo, fallbackSeo, originalUrl, currentLang, languages } = params;
  const fallback = fallbackSeo?.translations?.[0];

  const {
    title = fallback?.title,
    description = fallback?.description,
    ogDescription = fallback?.ogDescription,
    ogTitle = fallback?.ogTitle,
    ogImage = fallback?.ogImage,
    ogUrl = fallback?.ogUrl,
    robots = fallback?.robots,
    canonical = fallback?.canonical,
    twitter_card = fallback?.twitter_card,
    twitter_description = fallback?.twitter_description,
    twitter_image = fallback?.twitter_image,
    twitter_title = fallback?.twitter_title,
    jsonld = fallback?.jsonld,
  } = pageSeo?.translations?.[0] ?? {};

  const alternativeLinks = currentLang
    ? languages
        .filter((item) => item.code !== currentLang)
        .map((item) => {
          return {
            tagName: "link",
            rel: "alternate",
            hrefLang: item.code,
            href: originalUrl.replace(
              new RegExp(`/${currentLang}(?=/|$)`),
              `/${item.code}`,
            ),
          };
        })
    : [];

  const result: Array<MetaDescriptor> = [
    { title: override?.title ?? title },
    { name: "description", content: override?.description ?? description },
    { property: "og:title", content: override?.ogTitle ?? ogTitle },
    {
      property: "og:description",
      content: override?.ogDescription ?? ogDescription,
    },
    { property: "og:image", content: override?.ogImage ?? ogImage },
    { property: "og:url", content: override?.ogUrl ?? ogUrl },
    { property: "robots", content: override?.robots ?? robots },
    {
      property: "canonical",
      content: override?.canonical ?? canonical ?? originalUrl,
    },
    { name: "twitter:card", content: override?.twitter_card ?? twitter_card },
    {
      name: "twitter:description",
      content: override?.twitter_description ?? twitter_description,
    },
    {
      name: "twitter:image",
      content: override?.twitter_image ?? twitter_image,
    },
    {
      name: "twitter:title",
      content: override?.twitter_title ?? twitter_title,
    },
    ...alternativeLinks,
  ];

  if (override?.jsonld || jsonld) {
    result.push({
      "script:ld+json": override?.jsonld ?? jsonld,
    });
  }
  return result;
}
